import { useEffect, useRef } from "react"
import { Avatar } from "@/components/ui/avatar"

interface ProfileMediaItem {
  isCover: boolean
  upload?: {
    id: string
    filePath: string
  }
}

export interface MentionUser {
  id: string
  name: string
  username: string
  profileMedia?: ProfileMediaItem[]
}

const getUserAvatar = (user: MentionUser): string | undefined => {
  const coverMedia = user.profileMedia?.find(media => media.isCover && media.upload)
  return coverMedia?.upload?.filePath
}

interface MentionSuggestionProps {
  users: MentionUser[]
  selectedIndex: number
  isLoading: boolean
  onSelect: (user: MentionUser) => void
  onHover: (index: number) => void
}

export function MentionSuggestion({
  users,
  selectedIndex,
  isLoading,
  onSelect,
  onHover,
}: MentionSuggestionProps) {
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest" })
      }
    }
  }, [selectedIndex])

  if (isLoading) {
    return (
      <div className="absolute top-full left-0 mt-2 w-full min-h-[80px] rounded-lg border border-border bg-background shadow-lg overflow-hidden z-50">
        <div className="flex items-center justify-center py-6">
          <div className="size-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className="absolute top-full left-0 mt-2 w-full min-h-[60px] rounded-lg border border-border bg-background shadow-lg overflow-hidden z-50">
        <p className="text-sm text-muted-foreground text-center py-4">No users found</p>
      </div>
    )
  }

  return (
    <div
      ref={listRef}
      className="absolute top-full left-0 mt-2 w-full max-h-[200px] overflow-y-auto rounded-lg border border-border bg-background shadow-lg z-50"
    >
      {users.map((user, index) => (
        <button
          key={user.id}
          type="button"
          onClick={() => onSelect(user)}
          onMouseEnter={() => onHover(index)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors cursor-pointer ${
            index === selectedIndex ? "bg-accent" : "hover:bg-accent/50"
          }`}
        >
          <Avatar src={getUserAvatar(user)} fallback={user.name} size="sm" className="w-8 h-8" />
          <div className="text-left min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground truncate">@{user.username}</p>
          </div>
        </button>
      ))}
    </div>
  )
}
